import React, { useEffect, useRef, useState } from "react";
import { Menu, X } from "lucide-react";
import Brand from "../brand/Brand";
import { useI18n } from "../i18n";
import { getPageData } from "./pageData";

const isExternal = (href) =>
  /^https?:\/\//.test(href) || (href && href.endsWith(".pdf"));

function LangSwitch({ compact }) {
  const { locale, setLocale, locales } = useI18n();
  return (
    <div className={`nfw-lang${compact ? " nfw-lang-compact" : ""}`}>
      {locales.map((item) => (
        <button
          key={item.id}
          type="button"
          className={locale === item.id ? "active" : ""}
          onClick={() => setLocale(item.id)}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

const Header3 = () => {
  const { t } = useI18n();
  const dataSource = getPageData(t).nav;
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(-1);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onDoc = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenMenu(-1);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const go = (href) => {
    if (!href) return;
    setOpenMenu(-1);
    setMobileOpen(false);
    if (isExternal(href)) {
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }
    window.location.href = href;
  };

  const cta = dataSource.menu.cta;

  return (
    <nav className="nfw-nav" ref={navRef}>
      <div
        className={`nfw-nav-bar nfw-glass${scrolled ? " nfw-nav-scrolled" : ""}`}
      >
        <div className="nfw-nav-left">
          <Brand />

          <div className="nfw-nav-links">
            {dataSource.menu.items.map((item, idx) => {
              if (!item.children) {
                return (
                  <div className="nfw-nav-item" key={item.name}>
                    <a className="nfw-nav-link" href={item.href}>
                      {item.name}
                    </a>
                  </div>
                );
              }
              return (
                <div
                  className={`nfw-nav-item nfw-nav-drop${
                    openMenu === idx ? " open" : ""
                  }`}
                  key={item.name}
                  onMouseEnter={() => setOpenMenu(idx)}
                  onMouseLeave={() => setOpenMenu(-1)}
                >
                  <button
                    type="button"
                    className="nfw-nav-link"
                    aria-expanded={openMenu === idx}
                    onClick={() => setOpenMenu(openMenu === idx ? -1 : idx)}
                  >
                    {item.name}
                  </button>
                  <div className="nfw-nav-panel nfw-glass">
                    {item.children.map((child) => (
                      <a
                        key={child.name}
                        href={child.href}
                        target={isExternal(child.href) ? "_blank" : undefined}
                        rel={
                          isExternal(child.href)
                            ? "noopener noreferrer"
                            : undefined
                        }
                        onClick={(event) => {
                          if (isExternal(child.href)) return;
                          event.preventDefault();
                          go(child.href);
                        }}
                      >
                        <strong>{child.name}</strong>
                        {child.desc && <span>{child.desc}</span>}
                      </a>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="nfw-nav-cta">
          <LangSwitch compact />
          {cta && (
            <a className="nfw-btn nfw-btn-solid" href={cta.href}>
              {cta.name}
            </a>
          )}
          <button
            type="button"
            className="nfw-nav-toggle"
            aria-label={t.nav.openMenu}
            onClick={() => setMobileOpen(true)}
          >
            <Menu size={22} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="nfw-nav-mobile">
          <button
            type="button"
            className="nfw-nav-mobile-close"
            aria-label={t.nav.closeMenu}
            onClick={() => setMobileOpen(false)}
          >
            <X size={28} />
          </button>
          {dataSource.menu.items.map((item) =>
            item.children ? (
              <React.Fragment key={item.name}>
                <div className="nfw-nav-mobile-label">{item.name}</div>
                {item.children.map((child) => (
                  <a
                    key={child.name}
                    href={child.href}
                    target={isExternal(child.href) ? "_blank" : undefined}
                    rel={
                      isExternal(child.href) ? "noopener noreferrer" : undefined
                    }
                    onClick={(event) => {
                      if (isExternal(child.href)) {
                        setMobileOpen(false);
                        return;
                      }
                      event.preventDefault();
                      go(child.href);
                    }}
                  >
                    {child.name}
                  </a>
                ))}
              </React.Fragment>
            ) : (
              <a
                key={item.name}
                href={item.href}
                onClick={() => setMobileOpen(false)}
              >
                {item.name}
              </a>
            )
          )}
          {cta && (
            <a className="nfw-btn nfw-btn-solid" href={cta.href}>
              {cta.name}
            </a>
          )}
          <LangSwitch />
        </div>
      )}
    </nav>
  );
};

export default Header3;
